import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { BarChart, TrendingUp, Users, Heart } from "lucide-react";

export default function Analytics() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const loadUser = async () => {
      const { data: { session } } = await digitalOcean.auth.getSession();
      if (!session) {
        navigate("/Auth");
        return;
      }
      const { data: profile } = await digitalOcean
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();
      setCurrentUser(profile);
    };
    loadUser();
  }, [navigate]);

  const { data: trackStats } = useQuery({
    queryKey: ["track-analytics", currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return null;

      // Runtime check: Verify digitalOcean client exposes Supabase-compatible methods
      if (typeof digitalOcean.from !== 'function') {
        const error = new Error('DigitalOcean client does not expose .from() method - incompatible with Supabase-style queries');
        console.error(error.message);
        throw error;
      }

      try {
        const { data: tracks } = await digitalOcean
          .from("tracks")
          .select("*")
          .eq("artist_id", currentUser.id);

        if (!tracks) return null;

        const totalPraise = tracks.reduce((sum, t) => sum + (t.praise_count || 0), 0);
        const totalScathe = tracks.reduce((sum, t) => sum + (t.scathe_count || 0), 0);
        const totalFeedback = tracks.reduce((sum, t) => sum + (t.praise_count || 0) + (t.neutral_count || 0) + (t.scathe_count || 0), 0);

        return {
          totalTracks: tracks.length,
          totalPraise,
          totalScathe,
          totalFeedback,
        };
      } catch (error) {
        console.error('Error fetching track analytics:', error);
        throw error; // Re-throw to let React Query handle the error state
      }
    },
    enabled: !!currentUser,
  });

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <BarChart className="w-8 h-8" />
            Analytics
          </h1>
          <Button variant="outline" onClick={() => navigate("/MyTracks")} tooltip="Return to your tracks page">
            Back to My Tracks
          </Button>
        </div>

        {trackStats && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-card p-6 rounded-lg border border-border">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-muted-foreground">Total Tracks</h3>
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div className="text-3xl font-bold text-foreground">{trackStats.totalTracks}</div>
            </div>

            <div className="bg-card p-6 rounded-lg border border-border">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-muted-foreground">Total Praise</h3>
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <div className="text-3xl font-bold text-foreground">{trackStats.totalPraise}</div>
            </div>

            <div className="bg-card p-6 rounded-lg border border-border">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-muted-foreground">Total Scathe</h3>
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
              <div className="text-3xl font-bold text-foreground">{trackStats.totalScathe}</div>
            </div>
          </div>
        )}

        <div className="bg-card p-8 rounded-lg border border-border text-center">
          <BarChart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold text-card-foreground mb-2">
            Detailed Analytics Coming Soon
          </h2>
          <p className="text-muted-foreground mb-6">
            Track engagement over time, demographic insights, peak listening hours, and more advanced metrics will be available soon.
          </p>
          <Button onClick={() => navigate("/MyTracks")}>View My Tracks</Button>
        </div>
      </div>
    </div>
  );
}
